import { Suspense, useCallback, useMemo, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { V2Skeleton, V2Stepper as Stepper } from "@stacklet/ui";
import { ErrorBoundary } from "react-error-boundary";
import { FormProvider, useForm } from "react-hook-form";
import { graphql, useFragment } from "react-relay";
import { useLocation, useNavigate } from "react-router";

import { useNotificationRuleFormContext } from "../contexts/NotificationRuleFormContext";
import { mapNotificationRuleToFormValues } from "../utils/mapNotificationRuleToFormValues";
import { RulesDeliveryMethod } from "./RulesDeliveryMethod";
import { RulesFilters } from "./RulesFilters";
import { notificationRuleFormSchema, type RulesFilterFormData } from "./schema";

import type { UpsertNotificationRuleStepsContainer_notificationRule$key } from "./__generated__/UpsertNotificationRuleStepsContainer_notificationRule.graphql";

const NOTIFICATION_RULE_STEPS = [
  { id: "filters", label: "Filters" },
  { id: "delivery", label: "Delivery Method" },
];

interface Props {
  notificationRule?: UpsertNotificationRuleStepsContainer_notificationRule$key;
}

export function UpsertNotificationRuleStepsContainer({
  notificationRule,
}: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(0);
  const { isEditMode, ruleUUID } = useNotificationRuleFormContext();
  const data = useFragment(
    graphql`
      fragment UpsertNotificationRuleStepsContainer_notificationRule on NotificationRule {
        uuid
        name
        description
        filterJSON
        groupBy
        includeCostData
        updateSchedule
        target {
          ... on NotificationRuleJiraTarget {
            instance {
              uuid
            }
            space {
              type
              value
            }
            workType
            assignee {
              type
              value
            }
            description
          }
        }
      }
    `,
    notificationRule,
  );
  const mappedRule = useMemo(() => {
    return data
      ? {
          uuid: data.uuid,
          name: data.name,
          description: data.description,
          filterJSON: data.filterJSON,
          groupBy: [...data.groupBy],
          includeCostData: data.includeCostData,
          updateSchedule: data.updateSchedule,
          target: data.target
            ? {
                ...(data.target.assignee
                  ? {
                      assignee: {
                        type: data.target.assignee.type,
                        value: data.target.assignee.value,
                      },
                    }
                  : {}),
                ...(data.target.space
                  ? {
                      space: {
                        type: data.target.space.type,
                        value: data.target.space.value,
                      },
                    }
                  : {}),
                ...(data.target.instance !== undefined
                  ? { instance: data.target.instance }
                  : {}),
                ...(data.target.description !== undefined
                  ? { description: data.target.description }
                  : {}),
                ...(data.target.workType !== undefined
                  ? { workType: data.target.workType }
                  : {}),
              }
            : null,
        }
      : null;
  }, [data]);

  const initialValues = useMemo(() => {
    // Edit mode: map API rule shape -> form shape (dropdown-friendly).
    // Create mode: fall back to empty defaults.
    if (isEditMode && ruleUUID) {
      return mapNotificationRuleToFormValues(mappedRule);
    }

    return mapNotificationRuleToFormValues(null);
  }, [mappedRule, isEditMode, ruleUUID]);

  const methods = useForm<RulesFilterFormData>({
    resolver: zodResolver(notificationRuleFormSchema),
    defaultValues: initialValues,
  });

  const navigateToRules = useCallback(() => {
    const { state } = location;
    const from = typeof state?.from === "string" ? state.from : undefined;
    if (from?.startsWith("/notifications/")) {
      navigate(-1);
    } else {
      navigate("/notifications/rules");
    }
  }, [location.state, navigate]);

  const stepContent =
    currentStep === 0 ? (
      <Suspense
        fallback={
          <V2Skeleton
            aria-label="Loading filters…"
            className="mt-lg"
            height="24rem"
            variant="rectangular"
          />
        }
      >
        <RulesFilters
          onCancel={navigateToRules}
          onNextStep={() => setCurrentStep(1)}
        />
      </Suspense>
    ) : (
      <Suspense
        fallback={
          <V2Skeleton
            aria-label="Loading delivery method…"
            className="mt-lg"
            height="24rem"
            variant="rectangular"
          />
        }
      >
        <RulesDeliveryMethod onCancel={navigateToRules} />
      </Suspense>
    );

  return (
    <FormProvider {...methods}>
      <Stepper
        currentStep={currentStep}
        onStepClick={setCurrentStep}
        steps={NOTIFICATION_RULE_STEPS}
        allowSkipping
      />
      <ErrorBoundary fallback={<div>Error loading step</div>}>
        {stepContent}
      </ErrorBoundary>
    </FormProvider>
  );
}
